/**
 * WebSocket 插件
 * @param {Object} options 配置选项
 *   - url: WebSocket 服务器地址 (必需)
 *   - protocols: 协议字符串或字符串数组
 *   - reconnectInterval: 重连间隔(ms)，默认 3000
 *   - maxReconnectAttempts: 最大重连次数，默认无限
 *   - heartbeatInterval: 心跳间隔(ms)，默认 15000
 *   - heartbeatMsg: 心跳消息内容，默认 'ping'
 */
class WebSocketPlugin {
  constructor(options) {
    if (!options.url) throw new Error('WebSocket url is required')

    this.options = {
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      heartbeatInterval: 15000,
      heartbeatMsg: 'ping',
      ...options
    }

    this.socket = null
    this.reconnectAttempts = 0
    this.eventListeners = {}
    this.heartbeatTimer = null
    this.isManualClose = false

    this.connect()
  }

  // 建立连接
  connect() {
    this.isManualClose = false

    try {
      this.socket = this.options.protocols
        ? new WebSocket(this.options.url, this.options.protocols)
        : new WebSocket(this.options.url)

      this.bindEvents()
    } catch (error) {
      console.error('WebSocket connection error:', error)
      this.reconnect()
    }
  }

  // 绑定事件
  bindEvents() {
    this.socket.onopen = event => {
      console.log('WebSocket connected')
      this.reconnectAttempts = 0
      this.emit('open', event)
      this.startHeartbeat()
    }

    this.socket.onmessage = event => {
      this.emit('message', event.data)

      // 重置心跳计时器
      if (this.heartbeatTimer) {
        clearTimeout(this.heartbeatTimer)
        this.startHeartbeat()
      }
    }

    this.socket.onclose = event => {
      console.log('WebSocket closed:', event.code, event.reason)
      this.emit('close', event)
      this.stopHeartbeat()

      if (!this.isManualClose) {
        this.reconnect()
      }
    }

    this.socket.onerror = error => {
      console.error('WebSocket error:', error)
      this.emit('error', error)
    }
  }

  // 发送消息
  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(typeof data === 'string' ? data : JSON.stringify(data))
      return true
    }

    console.warn('WebSocket is not connected')
    return false
  }

  // 关闭连接
  close(code, reason) {
    this.isManualClose = true
    if (this.socket) {
      this.socket.close(code || 1000, reason)
    }
  }

  // 重新连接
  reconnect() {
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      return
    }

    this.reconnectAttempts++
    console.log(`Attempting to reconnect (${this.reconnectAttempts})...`)

    setTimeout(() => {
      this.connect()
    }, this.options.reconnectInterval)
  }

  // 心跳检测
  startHeartbeat() {
    if (!this.options.heartbeatInterval) return

    this.heartbeatTimer = setTimeout(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.send(this.options.heartbeatMsg)
      }
    }, this.options.heartbeatInterval)
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  // 事件监听
  on(event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = []
    }
    this.eventListeners[event].push(callback)
  }

  off(event, callback) {
    if (!this.eventListeners[event]) return

    if (callback) {
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback)
    } else {
      delete this.eventListeners[event]
    }
  }

  emit(event, ...args) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(callback => {
        try {
          callback(...args)
        } catch (err) {
          console.error(`Error in ${event} event handler:`, err)
        }
      })
    }
  }

  // 获取当前状态
  getState() {
    return this.socket ? this.socket.readyState : WebSocket.CLOSED
  }
}

export default WebSocketPlugin
